import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS, apiCall } from '../config/api';
import { useNavigate } from 'react-router-dom';
import { pickEmailToFetchPdf } from '../components/common/EmailListInput';

const AuthContext = createContext();

// Token encoding/decoding functions
const encodeToken = (token) => {
  return btoa(btoa(token)); // Double encode for extra security
};

const decodeToken = (encodedToken) => {
  try {
    return atob(atob(encodedToken)); // Double decode
  } catch (error) {
    console.error('Token decode error:', error);
    return null;
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allUsers, setAllUsers] = useState([]);

  useEffect(() => {
    const _host = window.location.hostname;
    const _isLocalhost = _host === 'localhost' || _host === '127.0.0.1';
    const _urlParams = new URLSearchParams(window.location.search);
    const _loginCheck = _urlParams.get('_logincheck') === '1';
    // quicklook.in stamps this on the bounce-back so the login domain knows the
    // check already happened. document.referrer alone isn't enough: Safari's
    // ITP strips it on cross-site JS redirects, which used to make the two
    // domains ping-pong forever.
    const _fromQuicklook = _urlParams.get('_from') === 'quicklook';
    let _referrerHost = '';
    let _referrerHasLocalLoginCheck = false;
    try {
      if (document.referrer) {
        const _refUrl = new URL(document.referrer);
        _referrerHost = _refUrl.hostname;
        _referrerHasLocalLoginCheck = _refUrl.searchParams.get('_logincheck') === '1';
      }
    } catch (e) {
      // ignore invalid referrer parsing
    }

    // ── quicklook.in pe _logincheck param handle karo ──
    // client.hmrspedition.com ne check karne bheja tha — user logged in hai ya nahi?
    if (_loginCheck) {
      const savedUser = localStorage.getItem('user');
      const savedToken = localStorage.getItem('token');
      if (savedUser && savedToken && decodeToken(savedToken)) {
        // Logged in hai → clean URL aur dashboard dikhao
        window.history.replaceState({}, document.title, window.location.pathname);
        // Normal flow continue hone do (neeche localStorage check karega)
      } else {
        // Logged in nahi → wapas login domain pe bhejo
        if (_isLocalhost) {
          const port = window.location.port ? `:${window.location.port}` : '';
          window.location.href = `http://localhost${port}/?_from=quicklook`;
        } else {
          window.location.href = 'https://client.hmrspedition.com/?_from=quicklook';
        }
        return;
      }
    }

    // ── Login domain pe localStorage clear karo aur Login dikhao ──
    // Agar quicklook.in se wapas aaye ho (login check ke baad / logout flow),
    // to seedha login page dikhao. Warna pehle quicklook.in pe login-check karo.
    if (_host === 'client.hmrspedition.com') {
      localStorage.removeItem('user');
      localStorage.removeItem('token');

      // Loop breaker: remember (per tab, briefly) that we already sent the
      // user to quicklook.in for a login check, so even if both the ?_from
      // param and the referrer are lost we never bounce a second time.
      const LOGIN_CHECK_KEY = 'ql_logincheck_sent_at';
      const LOGIN_CHECK_TTL_MS = 30 * 1000;
      let _checkRecentlySent = false;
      try {
        const sentAt = Number(sessionStorage.getItem(LOGIN_CHECK_KEY) || 0);
        _checkRecentlySent = sentAt > 0 && Date.now() - sentAt < LOGIN_CHECK_TTL_MS;
      } catch (e) {
        // sessionStorage unavailable (privacy mode) — fall through to the other signals
      }

      const _returnedFromQuicklook =
        _fromQuicklook ||
        _checkRecentlySent ||
        _referrerHost === 'quicklook.in' ||
        (_isLocalhost && _referrerHasLocalLoginCheck);

      if (!_returnedFromQuicklook) {
        try { sessionStorage.setItem(LOGIN_CHECK_KEY, String(Date.now())); } catch (e) { /* ignore */ }
        // quicklook.in pe jao aur check karo — user wahan logged in ho sakta hai
        if (_isLocalhost) {
          const port = window.location.port ? `:${window.location.port}` : '';
          window.location.href = `http://localhost${port}/?_logincheck=1`;
        } else {
          window.location.href = 'https://quicklook.in/?_logincheck=1';
        }
        return;
      }

      // quicklook.in se return hua ho to login page dikhao (clean URL).
      // The sessionStorage stamp is deliberately left in place: once the URL
      // is cleaned the ?_from signal is gone, and this effect can run again
      // (StrictMode, remount) — the stamp's TTL covers that window.
      if (_fromQuicklook) {
        try { sessionStorage.setItem(LOGIN_CHECK_KEY, String(Date.now())); } catch (e) { /* ignore */ }
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      setLoading(false);
      return;
    }

    // On a single-domain setup (localhost) the ?_from marker has no reader —
    // just keep the URL tidy.
    if (_fromQuicklook) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // ── Cross-domain auth handoff ──
    // Agar quicklook.in pe ?_auth=... param hai (client.hmrspedition.com se redirect hua)
    const urlParams = new URLSearchParams(window.location.search);
    const authParam = urlParams.get('_auth');
    if (authParam) {
      try {
        const payload = JSON.parse(decodeURIComponent(atob(atob(authParam))));
        if (payload.user && payload.token) {
          const normalizedUser = {
            ...payload.user,
            profilePicture: payload.user.profilePicture || payload.user.profilePic || payload.user.profile_pic
          };
          localStorage.setItem('user', JSON.stringify(normalizedUser));
          localStorage.setItem('token', encodeToken(payload.token));
          setUser(normalizedUser);
          // URL se _auth param hata do (clean URL)
          window.history.replaceState({}, document.title, window.location.pathname);
          setLoading(false);
          return;
        }
      } catch (e) {
        console.error('Auth handoff parse error:', e);
      }
    }

    const savedUser = localStorage.getItem('user');
    const encodedToken = localStorage.getItem('token');
    
    if (savedUser && encodedToken) {
      const token = decodeToken(encodedToken);

      if (token) {
        const userData = JSON.parse(savedUser);
        const normalizedUser = {
          ...userData,
          profilePicture: userData.profilePicture || userData.profilePic || userData.profile_pic
        };
        setUser(normalizedUser);
        setLoading(false);
      } else {
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async (token) => {
    let authToken = token;
    if (!authToken) {
      const encodedToken = localStorage.getItem('token');
      authToken = encodedToken ? decodeToken(encodedToken) : null;
    }
    if (!authToken) return;
    
    try {
      const response = await apiCall(API_ENDPOINTS.GET_USERS, {
        method: 'GET'
      });

      const data = await response.json();

      if (response.ok && response.status === 200) {
        console.log('API Response Users:', data.data?.users || data.users || data.data);
        const users = (data.data?.users || data.users || data.data || []).map(u => ({
          id: u.id,
          name: u.custName || u.name || u.username,
          email: u.email,
          mobile: u.ph_num || u.phNum || u.phoneNumber || u.mobile,
          username: u.username,
          password: u.password || '',
          role: u.role,
          status: u.status,
          website: u.website,
          emailToFetchPdf: pickEmailToFetchPdf(u),
          created_by: u.created_by ?? u.createdBy ?? null
        }));
        console.log('Mapped Users with Password:', users);
        setAllUsers(users);
      }
    } catch (err) {
      console.error('Fetch users error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const login = (userData, token) => {
    console.log('=== AuthContext: Login called ===');
    console.log('User data:', userData);
    console.log('Token received:', token ? 'Yes (length: ' + token.length + ')' : 'No');
    
    // Normalize profilePic field to profilePicture for consistency
    const normalizedUser = {
      ...userData,
      profilePicture: userData.profilePicture || userData.profilePic || userData.profile_pic
    };

    const currentHost = window.location.hostname;
    const isLocalhost = currentHost === 'localhost' || currentHost === '127.0.0.1';
    const isLoginDomain = currentHost === 'client.hmrspedition.com';

    // LOGIN DOMAIN pe localStorage mein kuch mat bachao — loop avoid karne ke liye
    // Sirf dashboard domain pe token/user save hoga
    if (!isLoginDomain) {
      setUser(normalizedUser);
      localStorage.setItem('user', JSON.stringify(normalizedUser));
      console.log('User saved to localStorage');
      if (token) {
        const encodedToken = encodeToken(token);
        localStorage.setItem('token', encodedToken);
        console.log('Token saved to localStorage');
      }
    }

    console.log('=== Login complete ===');

    // login domain ya local login mode se → _auth token ke saath dashboard pe redirect karo
    const payload = JSON.stringify({ user: normalizedUser, token });
    const encoded = btoa(btoa(encodeURIComponent(payload)));

    if (isLoginDomain) {
      window.location.href = `https://quicklook.in/?_auth=${encoded}`;
    }
  };

  const updateUser = (userData) => {
    // Normalize profilePic field to profilePicture for consistency
    const normalizedUser = {
      ...userData,
      profilePicture: userData.profilePicture || userData.profilePic || userData.profile_pic
    };
    setUser(normalizedUser);
    localStorage.setItem('user', JSON.stringify(normalizedUser));
  };

  const logout = async () => {
    // Store logout time before clearing user data
    if (user && user.id) {
      const logoutTime = new Date().toISOString();
      localStorage.setItem(`lastLogout_${user.id}`, logoutTime);
      console.log('Logout time stored in AuthContext:', logoutTime, 'for user:', user.id);
    }
    
    // Call backend logout API
    try {
      await apiCall(API_ENDPOINTS.LOGOUT, {
        method: 'POST'
      });
      console.log('Backend logout successful');
    } catch (error) {
      console.error('Backend logout failed:', error);
      // Continue with local logout even if API fails
    }
    
    setUser(null);
    setAllUsers([]);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    // Logout ke baad login page pe bhejo (?_from so the login domain doesn't
    // bounce back here for a session check we already know is empty)
    const currentHost = window.location.hostname;
    const isLocalhost = currentHost === 'localhost' || currentHost === '127.0.0.1';
    if (isLocalhost) {
      const port = window.location.port ? `:${window.location.port}` : '';
      window.location.href = `http://localhost${port}/?_from=quicklook`;
    } else {
      window.location.href = 'https://client.hmrspedition.com/?_from=quicklook';
    }
  };

  const getToken = () => {
    const encodedToken = localStorage.getItem('token');
    return encodedToken ? decodeToken(encodedToken) : null;
  };

  const value = {
    user,
    login,
    logout,
    loading,
    isAuthenticated: !!user,
    getToken,
    updateUser,
    allUsers,
    setAllUsers,
    fetchUsers,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
